// "Scheduled" is DERIVED, never a status — there is deliberately no SCHEDULED enum value on the wire.
// A task the agent will only pick up later is an ordinary PENDING task carrying a future `notBefore`, so
// a surface that never implements this still renders the row correctly as pending work. Shared 1:1 with
// iOS/Android; keep the rule here and nowhere else.
//
// Import-free on purpose (see orion-web/CLAUDE.md → Tests): the bucket map, the card and the list all read
// it, and a jest test must be able to exercise it without dragging the app's dependency graph in.

/** The minimum a row needs for the scheduled rule. Satisfied by `AgentTask` and by web's `WebTask`. */
export interface SchedulableTask {
  status?: string;
  /** ISO timestamp the agent may not start before. Absent on everything that runs now. */
  notBefore?: string | Date;
  /**
   * The worker agent's own conversation — the durable record that an agent has BEEN PUT ON this task.
   * Read here only by {@link isNotStartedTask}; see that function for why it is the right signal.
   */
  agentConversationId?: string;
}

/** `notBefore` as a sortable number; `null` when absent or unparseable — never a throw, never a 0. */
export function notBeforeMs(task: SchedulableTask): number | null {
  if (!task.notBefore) return null;
  const ms = new Date(task.notBefore).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * scheduled := status == PENDING && notBefore != null && notBefore > now.
 *
 * Deliberately narrow: a PENDING row whose `notBefore` has passed is queued work the agent may take at any
 * moment, not something waiting for a date, so it reads as an ordinary live row.
 */
export function isScheduledTask(task: SchedulableTask, now: number = Date.now()): boolean {
  if (task.status !== 'PENDING') return false;
  const at = notBeforeMs(task);
  return at !== null && at > now;
}

/**
 * notStarted := status == PENDING && !scheduled && nobody has ever been put on it.
 *
 * The SIBLING of `isScheduledTask`, and derived for the same reason: there is no NOT_STARTED on the wire,
 * so a surface that never implements this still renders the row as the pending work it is.
 *
 * A task the agent deliberately laid out and has not begun buckets as In progress (PENDING is the agent's
 * turn, not the user's — see `bucketContaining`), which is correct for counting and wrong for reading: two
 * goal-laid-out tasks sat on a real Tasks screen saying "In progress" with no agent anywhere near them.
 *
 * **The "never started" half is a POSITIVE signal, not an absence.** `agentConversationId` is written when
 * a task's agent is dispatched (`dispatch-task-agent.ts`) and is deliberately KEPT after that loop dies
 * (`ensure-task-agent-running.ts` says so in as many words), so its presence is a durable "an agent has
 * been on this" record rather than a guess about current activity. Nothing else on the row can answer the
 * question: the changeLog label vocabulary (`AGENT_TASK_CHANGE_LABEL`) has no `started` entry, and
 * `runInFlight` answers "is a loop running NOW", which is false for a task that ran yesterday and stopped.
 * `createGoalTask` creates exactly this shape — PENDING, no agent conversation, "laid out but idle".
 *
 * Scheduled wins when both apply: a dated task's own quiet treatment already says why nothing is moving,
 * and it says something more useful (WHEN) than "not started" would.
 */
export function isNotStartedTask(task: SchedulableTask, now: number = Date.now()): boolean {
  if (task.status !== 'PENDING') return false;
  if (isScheduledTask(task, now)) return false;
  return !task.agentConversationId;
}

const WEEK_MS = 7 * 86400 * 1000;

/**
 * The scheduled row's chip: the short weekday ("Thu") while the date is inside the coming week, a short
 * date ("14 Mar") beyond it. The locale's own `toLocaleDateString` decides the day/month order, so an
 * `en-GB` reader gets "14 Mar" and an `en-US` one "Mar 14" — same rule the activity feed already follows.
 * Empty string on an absent/unparseable date, so a caller can render nothing rather than "Invalid Date".
 */
export function scheduledDateLabel(
  value: string | Date | undefined,
  now: number = Date.now(),
  locale?: string,
): string {
  const at = notBeforeMs({ notBefore: value });
  if (at === null) return '';
  const date = new Date(at);
  if (at - now < WEEK_MS) return date.toLocaleDateString(locale, { weekday: 'short' });
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
